import { useEffect, useRef, useState } from 'react';

import { ErrorFillIcon } from '@thefarmersfront/kitchen-mobile';
import clsx from 'clsx';

import styles from './ImageWithGuard.module.scss';

/** @description 이미지 로드 실패 시 대체 이미지를 표시하는 컴포넌트 */
const ImageWithGuard = ({
  className,
  alt,
  src,
  width,
  height,
  skeletonConfig = null,
  errorMode = 'keep',
  fallbackElement,
}: {
  className?: string;
  alt?: string;
  src: string;
  width: number | string;
  height: number | string;
  skeletonConfig?: {
    show: boolean;
    delay: number;
  } | null;
  errorMode?: 'hide' | 'fallback' | 'keep';
  fallbackElement?: React.ReactNode;
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);

  const placeHolderTimerRef = useRef<NodeJS.Timeout | null>(null);

  const size = {
    width: Number.isNaN(Number(width)) ? width : `${width}px`,
    height: Number.isNaN(Number(height)) ? height : `${height}px`,
  };

  const clearPlaceHolderTimer = () => {
    if (placeHolderTimerRef.current) {
      clearTimeout(placeHolderTimerRef.current);
      placeHolderTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!skeletonConfig?.show) {
      return;
    }

    clearPlaceHolderTimer();

    if (!loaded && !error) {
      placeHolderTimerRef.current = setTimeout(() => {
        setShowPlaceholder(true);
      }, skeletonConfig?.delay);
    }

    if (loaded || error) {
      setShowPlaceholder(false);
    }
  }, [loaded, error, skeletonConfig?.show, skeletonConfig?.delay]);

  useEffect(() => {
    return () => {
      clearPlaceHolderTimer();
    };
  }, []);

  return (
    <>
      <img
        className={clsx(
          styles['image-container'],
          loaded && styles['loaded'],
          error && styles['error'],
          styles[errorMode],
          className
        )}
        loading="lazy"
        src={src}
        alt={alt}
        width={1}
        height={1}
        style={{
          ...((loaded || (error && errorMode === 'keep')) && {
            ...size,
          }),
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {showPlaceholder && (
        <div className={styles['placeholder-container']}>
          <div className={styles['placeholder']} style={{ ...size }} />
        </div>
      )}
      {error && errorMode === 'fallback' && <FallbackElement size={size} fallbackElement={fallbackElement} />}
    </>
  );
};

const FallbackElement = ({
  size,
  fallbackElement,
}: {
  size: { width: string | number; height: string | number };
  fallbackElement?: React.ReactNode;
}) => {
  if (fallbackElement) {
    return fallbackElement;
  }

  return (
    <div className={styles['error-container']} style={{ ...size }}>
      <ErrorFillIcon color="gray400" />
    </div>
  );
};

export default ImageWithGuard;
